import * as React from 'react';
import ListItemText from '@mui/material/ListItemText';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SxProps, Theme, useMediaQuery } from '@mui/material';
import './filter.css';
import { FilterOption } from '../../interfaces';

const MenuProps = {
  PaperProps: {
    style: {
      width: 200,
    },
  },
  MenuListProps: {
    autoFocusItem: false,
  },
};

type Props = {
  name: string;
  options: FilterOption[];
  state: string[];
  setState: React.Dispatch<React.SetStateAction<string[]>>;
  // eslint-disable-next-line react/require-default-props
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  emptyLabel?: string;
  allLabel?: string;
  sx?: SxProps<Theme>;
};

export default function MultiSelectFilter({
  name,
  options,
  state,
  setState,
  setLoading,
  emptyLabel,
  allLabel,
  sx,
}: Props) {
  const isDesktop = useMediaQuery('(min-width:900px)');
  const isAllSelected = options.length > 0 && state.length === options.length;

  // On select change, add the filtered option name to our state array
  const handleChange = (event: SelectChangeEvent<typeof state>) => {
    const {
      target: { value },
    } = event;
    if (value[value.length - 1] === 'all') {
      const allOptions = options.map(option => option.value);
      setState(state.length === options.length ? [] : allOptions);
      if (setLoading) {
        setLoading(true);
      }
      sessionStorage.setItem(
        name,
        state.length === options.length
          ? JSON.stringify([])
          : JSON.stringify(allOptions),
      );
      return;
    }
    if (setLoading) {
      setLoading(true);
    }
    setState(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
    sessionStorage.setItem(name, JSON.stringify(value));
  };

  return (
    <div>
      <FormControl sx={{ m: 1, width: isDesktop ? 150 : '100%', ...sx }}>
        <Select
          id="multi-select"
          multiple
          value={state}
          onChange={handleChange}
          input={<OutlinedInput />}
          autoWidth
          className="filter-select"
          renderValue={selected => {
            if (selected.length === 0) {
              return emptyLabel || `No ${name}`;
            }
            if (isAllSelected) {
              return allLabel || `All ${name}`;
            }
            const selectedLabels = selected.map(
              id => options.find(option => option.value === id)?.label,
            );
            return selectedLabels.join(', ');
          }}
          displayEmpty={!!emptyLabel}
          MenuProps={MenuProps}
          IconComponent={ExpandMoreIcon}>
          <MenuItem value="all">
            <Checkbox checked={isAllSelected} />
            <ListItemText primary={allLabel || `All ${name}`} />
          </MenuItem>
          {options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              <Checkbox
                checked={state.indexOf(option.value) > -1}
                sx={{
                  color: option.color || 'primary',
                  '&.Mui-checked': {
                    color: option.color || 'primary',
                  },
                }}
              />
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}
